import React from 'react';
import { useTheme } from '../../hooks';
import vulcanLogo from '../../assets/vulcan_v.png';

interface HeaderProps {
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
}

const Header: React.FC<HeaderProps> = ({ connectionStatus }) => {
  const { theme, toggleTheme } = useTheme();

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'bg-emerald-500';
      case 'connecting': return 'bg-amber-500 animate-pulse';
      case 'disconnected': return 'bg-red-500';
      default: return 'bg-gray-400 dark:bg-slate-500';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'disconnected': return 'Disconnected';
      default: return 'Unknown';
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 dark:bg-slate-950/85 backdrop-blur-md border-b border-gray-200 dark:border-slate-800">
      {/* Brand accent hairline */}
      <div className="h-0.5 bg-gradient-to-r from-red-600 via-red-600/60 to-transparent" aria-hidden="true"></div>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[62px] short:h-[46px]">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3">
            <img
              src={vulcanLogo}
              alt="Vulcan Logo"
              className="w-8 h-8 short:w-6 short:h-6 object-contain"
            />
            <div>
              <h1 className="text-lg short:text-base font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
                Vulcan View
              </h1>
              <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500 dark:text-slate-400 hidden sm:block short:hidden leading-tight">
                Mining Operations
              </p>
            </div>
          </div>

          {/* Status and Controls */}
          <div className="flex items-center space-x-3">
            {/* Connection Status */}
            <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 short:py-1 bg-gray-100 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-full">
              <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
              <span className="text-xs font-medium text-gray-700 dark:text-slate-300">
                {getStatusText()}
              </span>
            </div>

            {/* Mobile Status Indicator */}
            <div className="sm:hidden">
              <div className={`w-3 h-3 rounded-full ${getStatusColor()}`}></div>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-3 sm:p-2 short:p-1.5 rounded-lg bg-gray-100 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-800"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
